/**
 * @file 路由設定
 * @description 定義應用程式的路由配置，包含前台部落格與後台管理系統的路由結構
 */
import React, { Suspense } from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';

// 首屏靜態匯入
import PostList from 'pages/frontStage/posts/postList/PostList';
import { Error404 } from 'pages/errors';
import { RedirectUrl } from 'pages/redirectUrl';
import BloggerLayout from 'layout/bloggerLayout/BloggerLayout';
import Loading from 'components/loading/Loading';

// 非首屏 lazy 載入
const PostPage = React.lazy(() => import('pages/frontStage/posts/postPage/PostPage'));
const CategoryList = React.lazy(() => import('pages/frontStage/categories/categoryList/Categories'));
const CategoryPostsPage = React.lazy(() => import('pages/frontStage/categories/categoryPostsPage/CategoryPostsPage'));
const About = React.lazy(() => import('pages/frontStage/about/About'));
const Timeline = React.lazy(() => import('pages/frontStage/timeline/Timeline'));
const Login = React.lazy(() => import('pages/login/Login'));
const BackStageLayout = React.lazy(() => import('layout/backStageLayout/BackStageLayout'));
const BackStagePostList = React.lazy(() => import('pages/backStage/posts/postList/postList'));
const BackStagePostPageCRUD = React.lazy(() => import('pages/backStage/posts/postPageCRUD/postPageCRUD'));
const BackStageCategoryList = React.lazy(() => import('pages/backStage/category/categoryList/categoryList'));

import PersonOutlineIcon from 'assets/icons/PersonOutlineIcon';
import HomeBGI from 'assets/images/_DSC2436.webp';
import PostListBGI from 'assets/images/_DSC1080.webp';
import PostPageBGI from 'assets/images/_DSC2150.webp';
import AboutBGI from 'assets/images/_DSC1820.webp';
import CategoryBGI from 'assets/images/_DSC2740.webp';

// import AUTH_CODE from 'config/auth.code.config';
import localStorageUtil from 'utils/localStorageUtil';
import LocalStorageKeys from 'constants/localStorageKeys';
import NonAccess from './other/NonAccess';
import { PAGE_CHECK_AUTH } from 'config/config';

import routerPath from 'routes/router.path';

/** Lazy 元件包裝器 */
function LazyLoad({ children }: { children: React.ReactNode }): React.ReactNode {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

/** 路由設定介面 */
export interface RouteConfig {
  path?: string;
  index?: boolean;
  element?: React.ReactNode;
  errorElement?: React.ReactNode;
  children?: RouteConfig[];
  breadcrumbPath?: string;
  sidebarIcon?: React.ReactNode;
  pageTitle?: string;
  shortTitle?: string;
  authCode?: string | null;
  subPath?: string[];
  bannerImg?: string;
  hiddenFromNav?: boolean;
  breadcrumbParentPath?: string;
  breadcrumbParentTitle?: string;
  hiddenAsideCard?: boolean;
  tooltip?: string;
}

/** 使用者資訊介面 */
interface UserInfo {
  authItems?: string[];
  [key: string]: unknown;
}

/** 權限檢查元件 Props */
interface HasAuthProps {
  routeConfig: RouteConfig;
  originalElement: React.ReactNode;
}

/**
 * 權限檢查元件
 * @description 檢查使用者是否有權限存取該路由，若無權限則顯示 NonAccess 頁面
 */
function HasAuth({ routeConfig, originalElement }: HasAuthProps): React.ReactNode {
  const userInfo = localStorageUtil.getItem<UserInfo>(LocalStorageKeys.UserInfo);
  const checkAuth = !!userInfo && PAGE_CHECK_AUTH;
  const hasPermission =
    userInfo &&
    Array.isArray(userInfo.authItems) &&
    routeConfig.authCode &&
    userInfo.authItems.includes(routeConfig.authCode);
  if (checkAuth && routeConfig.authCode && !hasPermission) {
    return <NonAccess />;
  }
  return originalElement; // 使用傳遞的原始元件
}

/**
 * 遞迴套用權限檢查
 * @description 遍歷路由配置，為有 authCode 的路由套用權限檢查
 */
function applyHasAuth(route: RouteConfig): void {
  if (route.authCode && route.element) {
    const originalElement = route.element; // 保存原始元件
    route.element = <HasAuth routeConfig={route} originalElement={originalElement} />; // 傳遞原始元件
  }
  if (route.children) {
    route.children.forEach(applyHasAuth);
  }
}

export const routerConfig: RouteConfig[] = [
  {
    path: routerPath.home, // 路由的路徑
    element: <BloggerLayout bannerHeight='90' />, // 訪問時要渲染的元件
    errorElement: <Error404 />, // 有任何錯誤，例如無效的路由，就會渲染這個元件
    children: [
      // 子路由的陣列，它們會在訪問此路由時進一步導航
      /**
       ** 首頁
       **/
      {
        index: true, // 表示當路徑為 '/' 時，這個路由會被使用
        path: routerPath.home, // 子路由的路徑
        breadcrumbPath: '', // 頁面路由，用於麵包屑
        element: <PostList />,
        pageTitle: '首頁', // 可以添加首頁的標題
        bannerImg: HomeBGI // 首頁的背景圖片
      },
      /**
       ** posts 文章首頁列表
       **/
      {
        path: routerPath.posts, // 子路由的路徑
        breadcrumbPath: routerPath.posts, // 頁面路由，用於麵包屑
        errorElement: <Error404 />, // 有任何錯誤，例如無效的子路由，就會渲染這個元件
        sidebarIcon: <PersonOutlineIcon color='white' size='24' />, // 手機版側邊欄中對應此路由的圖標
        pageTitle: '文章列表', // 此路由的頁面標題
        shortTitle: '文章列表', // 此路由的短標題
        authCode: null, // 權限代碼
        children: [
          // 子路由的子路由的陣列
          {
            index: true, // 預設路由，當訪問 '/posts' 時，會自動導航到此路由
            element: <Navigate to='postList' />, // 自動導航到 'list' 子路由
            hiddenFromNav: true // 導航欄中是否隱藏
          },
          {
            path: routerPath.postList,
            pageTitle: '文章列表',
            element: <PostList />,
            authCode: null,
            subPath: [routerPath.postPage], // 子路由的陣列
            bannerImg: PostListBGI
          },
          {
            path: routerPath.postPage,
            pageTitle: '文章頁',
            element: <LazyLoad><PostPage /></LazyLoad>,
            authCode: null,
            hiddenFromNav: true,
            breadcrumbParentPath: routerPath.postList, // 前一層的頁面路由，用於麵包屑
            breadcrumbParentTitle: '文章列表', // 前一層的頁面標題，用於麵包屑
            hiddenAsideCard: true, // 是否隱藏側邊欄小卡
            bannerImg: PostPageBGI
          }
        ]
      },
      /**
       ** timeline 文章時間列表
       **/
      {
        path: routerPath.timeline, // 子路由的路徑
        breadcrumbPath: routerPath.timeline, // 頁面路由，用於麵包屑
        element: <LazyLoad><Timeline /></LazyLoad>, // 當訪問 '/timeline' 路由時要渲染的元件
        errorElement: <Error404 />, // 有任何錯誤，例如無效的子路由，就會渲染這個元件
        sidebarIcon: <PersonOutlineIcon color='white' size='24' />, // 手機版側邊欄中對應此路由的圖標
        pageTitle: '時間軸', // 此路由的頁面標題
        shortTitle: '時間軸', // 此路由的短標題
        authCode: null // 權限代碼
      },
      /**
       ** categories 文章分類
       **/
      {
        path: routerPath.categories, // 子路由的路徑
        breadcrumbPath: routerPath.categories, // 頁面路由，用於麵包屑
        errorElement: <Error404 />, // 有任何錯誤，例如無效的子路由，就會渲染這個元件
        sidebarIcon: <PersonOutlineIcon color='white' size='24' />, // 手機版側邊欄中對應此路由的圖標
        pageTitle: '文章分類', // 此路由的頁面標題
        shortTitle: '分類', // 此路由的短標題
        authCode: null, // 權限代碼
        children: [
          // 子路由的子路由的陣列
          {
            index: true, // 預設路由，當訪問 '/posts' 時，會自動導航到此路由
            element: <Navigate to='categoryList' />, // 自動導航到 'list' 子路由
            hiddenFromNav: true // 導航欄中是否隱藏
          },
          {
            path: routerPath.categoryList,
            pageTitle: '分類列表',
            element: <LazyLoad><CategoryList /></LazyLoad>,
            authCode: null,
            subPath: [routerPath.categoryPostsPage], // 子路由的陣列
            bannerImg: CategoryBGI
          },
          {
            path: routerPath.categoryPostsPage,
            pageTitle: '分類文章頁',
            element: <LazyLoad><CategoryPostsPage /></LazyLoad>,
            authCode: null,
            hiddenFromNav: true,
            breadcrumbParentPath: routerPath.categoryList, // 前一層的頁面路由，用於麵包屑
            breadcrumbParentTitle: '分類列表', // 前一層的頁面標題，用於麵包屑
            bannerImg: CategoryBGI
          }
        ]
      },
      /**
       ** about 關於我
       **/
      {
        path: routerPath.about, // 子路由的路徑
        breadcrumbPath: routerPath.about, // 頁面路由，用於麵包屑
        element: <LazyLoad><About /></LazyLoad>, // 當訪問 '/about' 路由時要渲染的元件
        errorElement: <Error404 />, // 有任何錯誤，例如無效的子路由，就會渲染這個元件
        sidebarIcon: <PersonOutlineIcon color='white' size='24' />, // 手機版側邊欄中對應此路由的圖標
        pageTitle: '關於我', // 此路由的頁面標題
        shortTitle: '關於我', // 此路由的短標題
        authCode: null, // 權限代碼
        bannerImg: AboutBGI // 首頁的背景圖片
      }
    ]
  },
  {
    path: routerPath.secretDoor, // 路由的路徑
    element: <LazyLoad><BackStageLayout /></LazyLoad>, // 訪問時要渲染的元件
    errorElement: <Error404 />, // 有任何錯誤，例如無效的路由，就會渲染這個元件
    children: [
      /**
       ** 文章管理
       **/
      {
        path: routerPath.secretDoor_Post, // 子路由的路徑
        breadcrumbPath: 'post', // 頁面路由，用於麵包屑
        errorElement: <Error404 />, // 有任何錯誤，例如無效的子路由，就會渲染這個元件
        sidebarIcon: <PersonOutlineIcon color='white' size='24' />, // 側邊欄中對應此路由的圖標
        pageTitle: '文章管理', // 此路由的頁面標題
        shortTitle: '文章', // 此路由的短標題
        authCode: null, // 權限代碼
        children: [
          // 子路由的子路由的陣列
          {
            index: true, // 預設路由，當訪問 '/post' 時，會自動導航到此路由
            element: <Navigate to='postList' />, // 自動導航到 'postList' 子路由
            hiddenFromNav: true // 導航欄中是否隱藏
          },
          {
            path: routerPath.secretDoor_Post_PostList,
            pageTitle: '文章列表',
            element: <LazyLoad><BackStagePostList /></LazyLoad>,
            authCode: null
          },
          {
            path: routerPath.secretDoor_Post_PostPage,
            pageTitle: '文章檢視編輯頁',
            element: <LazyLoad><BackStagePostPageCRUD /></LazyLoad>,
            hiddenFromNav: true,
            authCode: null
          }
        ]
      },
      /**
       ** 分類管理
       **/
      {
        path: routerPath.secretDoor_Category,
        breadcrumbPath: 'category',
        errorElement: <Error404 />,
        sidebarIcon: <PersonOutlineIcon color='white' size='24' />,
        pageTitle: '分類管理',
        shortTitle: '分類',
        authCode: null,
        children: [
          {
            index: true,
            element: <Navigate to='categoryList' />,
            hiddenFromNav: true
          },
          {
            path: routerPath.secretDoor_Category_CategoryList,
            pageTitle: '分類列表',
            element: <LazyLoad><BackStageCategoryList /></LazyLoad>,
            authCode: null
          }
        ]
      }
    ]
  },
  {
    path: '/login', // 路由的路徑
    element: <LazyLoad><Login /></LazyLoad> // 當訪問 '/login' 路由時要渲染的元件
  },
  {
    path: '*', // 匹配任何未在前面定義的路徑
    element: <RedirectUrl /> // 當訪問任何未在前面定義的路徑時，將渲染此元件
  }
];

routerConfig.forEach(applyHasAuth);

// 使用 type assertion 轉換為 RouteObject[]
// RouteConfig 擴展了 RouteObject，但 TypeScript 對 index 屬性有嚴格限制
const router = createBrowserRouter(routerConfig as RouteObject[]);

export default router;
